import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { Property } from './property';

export type MandatoryFieldStatus = 'incomplete' | 'complete' | null;

export function getMandatoryFieldStatus(
  property: Property,
  control: AbstractControl | null
): MandatoryFieldStatus {
  if (!containsMandatoryFields(property)) {
    return null;
  }

  return hasIncompleteMandatoryField(property, control) ? 'incomplete' : 'complete';
}

function containsMandatoryFields(property: Property): boolean {
  // Check the backend summary flag and recursively inspect any loaded child properties.
  return property.hasMandatoryFields === true
    || property.schemaMetadata?.mandatory === true
    || property.properties?.some(containsMandatoryFields) === true;
}

function hasIncompleteMandatoryField(
  property: Property,
  control: AbstractControl | null
): boolean {
  // Walk mandatory properties alongside their controls; unloaded lazy groups stay incomplete
  // until their fields can be checked, while disabled mandatory controls do not block completion.
  // An unloaded lazy property relies on the backend summary flag.
  if (property.propertyType === 'lazyLoaded') {
    return property.hasMandatoryFields === true || property.schemaMetadata?.mandatory === true;
  }

  // Mandatory leaf controls are incomplete when missing or invalid and enabled.
  if (property.schemaMetadata?.mandatory === true) {
    return !control || (!control.disabled && control.invalid);
  }

  const mandatoryChildren = property.properties?.filter(containsMandatoryFields) ?? [];

  // Group child controls are looked up by each property's key.
  if (property.propertyType === 'group') {
    const groupControl = control instanceof UntypedFormGroup ? control : null;
    if (mandatoryChildren.some(child =>
      hasIncompleteMandatoryField(child, groupControl?.get(child.key) ?? null))) {
      return true;
    }
  } else if (property.propertyType === 'multiple') {
    const formArray = control instanceof UntypedFormArray ? control : null;
    if (formArray && mandatoryChildren.length === 1 && formArray.length > 1) {
      if (formArray.controls.some(childControl =>
        hasIncompleteMandatoryField(mandatoryChildren[0], childControl))) {
        return true;
      }
    } else if (mandatoryChildren.some((child, index) =>
      hasIncompleteMandatoryField(child, formArray?.at(index) ?? null))) {
      return true;
    }
  } else if (mandatoryChildren.some(child =>
    hasIncompleteMandatoryField(child, control))) {
    return true;
  }

  if (property.hasMandatoryFields === true && mandatoryChildren.length === 0) {
    return !control || (!control.disabled && control.invalid)
      || (property.properties?.length ?? 0) === 0;
  }

  return false;
}
